import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash2,
  ExternalLink,
  Server,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Play,
  Activity,
  Lock
} from 'lucide-react';
import { appsService, argoService } from '../services/api';
import toast from 'react-hot-toast';

function Apps() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingApp, setDeletingApp] = useState(null);
  const [deleteOptions, setDeleteOptions] = useState({
    deleteBuckets: false,
    deleteDatabase: false,
    force: false
  });
  const [argoStatus, setArgoStatus] = useState(null);
  const [argoLoading, setArgoLoading] = useState(false);
  const [restartingApp, setRestartingApp] = useState(null);
  const [argoAppName, setArgoAppName] = useState(null);
  const [k8sNamespace, setK8sNamespace] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadApps();

    // Auto-refresh ArgoCD status every 5 seconds
    const interval = setInterval(() => {
      loadArgoStatus(true); // true means background refresh (don't show full loader if we don't want to)
    }, 5000);

    return () => clearInterval(interval);
  }, [argoAppName, k8sNamespace]);

  const loadApps = async () => {
    try {
      setLoading(true);
      const data = await appsService.getAll();
      setApps(data.apps);
      // Also fetch ArgoCD status
      loadArgoStatus();
    } catch (error) {
      toast.error('Failed to load applications');
      console.error('Load apps error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadArgoStatus = async (isBackground = false) => {
    try {
      if (!isBackground) setArgoLoading(true);

      let currentAppName = argoAppName;
      if (!currentAppName) {
        const settings = await argoService.getSettings();
        currentAppName = settings.appName;
        setArgoAppName(currentAppName);
        setK8sNamespace(settings.namespace);
      }

      if (currentAppName) {
        const data = await argoService.getApplication(currentAppName);
        setArgoStatus(data.status);
      }
    } catch (error) {
      console.error('ArgoCD load error:', error);
    } finally {
      if (!isBackground) setArgoLoading(false);
    }
  };

  const handleRestart = async (appName) => {
    try {
      setRestartingApp(appName);

      await argoService.restartResource(argoAppName, appName, {
        kind: 'Deployment',
        namespace: k8sNamespace,
        group: 'apps',
        version: 'v1'
      });

      toast.success(`Restart for '${appName}' triggered successfully`);
      // Refresh status after a short delay
      setTimeout(loadArgoStatus, 2000);
    } catch (error) {
      toast.error(`Failed to restart '${appName}': ${error.message}`);
      console.error('Restart error:', error);
    } finally {
      setRestartingApp(null);
    }
  };

  const getAppArgoStatus = (appName) => {
    if (!argoStatus) return null;
    return argoStatus.resources.find(r => r.name === appName && r.kind === 'Deployment');
  };

  const handleDelete = async (app) => {
    if (deletingApp === app.name) {
      try {
        setActionLoading(true);
        await appsService.delete(app.name, deleteOptions);
        toast.success(`Application '${app.name}' deleted successfully`);
        setApps(apps.filter(a => a.name !== app.name));
        setDeletingApp(null);
        setDeleteOptions({ deleteBuckets: false, deleteDatabase: false, force: false });
      } catch (error) {
        toast.error('Failed to delete application');
        console.error('Delete app error:', error);
      } finally {
        setActionLoading(false);
      }
    } else {
      setDeletingApp(app.name);
    }
  };

  const cancelDelete = () => {
    if (actionLoading) return;
    setDeletingApp(null);
    setDeleteOptions({ deleteBuckets: false, deleteDatabase: false, force: false });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Applications
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your deployed applications
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          <button
            onClick={() => loadArgoStatus()}
            disabled={argoLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${argoLoading ? 'animate-spin' : ''}`} />
            Refresh Status
          </button>
          <Link
            to="/apps/new"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Application
          </Link>
        </div>
      </div>

      {/* Apps Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Application
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Host & Infrastructure
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Replicas
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ArgoCD Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {apps.map((app) => {
                const argoAppStatus = getAppArgoStatus(app.name);
                return (
                  <tr key={app.name} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-50 rounded-lg flex items-center justify-center">
                          <Server className="h-6 w-6 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-bold text-gray-900 flex items-center">
                            {app.name}
                            {app.protected && (
                              <span
                                title="Protected: cannot be deleted from the dashboard"
                                className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800"
                              >
                                <Lock className="w-3 h-3 mr-0.5" />
                                Protected
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 truncate max-w-[200px]" title={app.image}>
                            {app.image}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <span className="font-medium">{app.host}</span>
                        <a
                          href={`http://${app.host}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-primary-600 hover:text-primary-900"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                      <div className="text-xs text-gray-500">Port: {app.port}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {app.replicas} replica{app.replicas !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {argoLoading ? (
                        <div className="flex items-center text-xs text-gray-400">
                          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                          Refreshing...
                        </div>
                      ) : argoAppStatus ? (
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${argoAppStatus.status === 'Synced' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            <Activity className="w-3 h-3 mr-1" />
                            {argoAppStatus.status}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${argoAppStatus.health?.status === 'Healthy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                            {argoAppStatus.health?.status}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Not tracked</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleRestart(app.name)}
                          disabled={restartingApp === app.name}
                          title="Restart Deployment"
                          className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors disabled:opacity-50"
                        >
                          {restartingApp === app.name ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </button>

                        <Link
                          to={`/apps/${app.name}/edit`}
                          title="Edit Application"
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>

                        <div className="relative">
                          {deletingApp === app.name ? (
                            <div className="absolute right-0 bottom-full mb-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-20 p-4 text-left">
                              <h4 className="text-sm font-bold text-gray-900 mb-2 whitespace-normal line-clamp-1">
                                Delete {app.name}?
                              </h4>
                              <div className="space-y-2 mb-3">
                                <label className="flex items-center text-xs text-gray-600 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={deleteOptions.deleteBuckets}
                                    onChange={(e) => setDeleteOptions({
                                      ...deleteOptions,
                                      deleteBuckets: e.target.checked
                                    })}
                                    className="mr-2 rounded text-primary-600"
                                  />
                                  Delete MinIO buckets
                                </label>
                                <label className="flex items-center text-xs text-gray-600 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={deleteOptions.deleteDatabase}
                                    onChange={(e) => setDeleteOptions({
                                      ...deleteOptions,
                                      deleteDatabase: e.target.checked
                                    })}
                                    className="mr-2 rounded text-primary-600"
                                  />
                                  Delete database
                                </label>
                                {deleteOptions.deleteBuckets && (
                                  <label className="flex items-center text-xs text-red-600 cursor-pointer animate-in fade-in slide-in-from-top-1">
                                    <input
                                      type="checkbox"
                                      checked={deleteOptions.force}
                                      onChange={(e) => setDeleteOptions({
                                        ...deleteOptions,
                                        force: e.target.checked
                                      })}
                                      className="mr-2 rounded text-red-600 focus:ring-red-500"
                                    />
                                    Force delete (if not empty)
                                  </label>
                                )}
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleDelete(app)}
                                  disabled={actionLoading}
                                  className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-red-600 text-white text-xs font-bold rounded hover:bg-red-700 shadow-sm disabled:opacity-50"
                                >
                                  {actionLoading ? (
                                    <>
                                      <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                      Deleting...
                                    </>
                                  ) : (
                                    'Confirm'
                                  )}
                                </button>
                                <button
                                  onClick={cancelDelete}
                                  disabled={actionLoading}
                                  className="flex-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-bold rounded hover:bg-gray-200 disabled:opacity-25"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : app.protected ? (
                            <span
                              title="Protected application: it cannot be deleted from the dashboard"
                              className="p-1.5 inline-flex text-gray-300 cursor-not-allowed"
                            >
                              <Lock className="w-4 h-4" />
                            </span>
                          ) : (
                            <button
                              onClick={() => handleDelete(app)}
                              title="Delete Application"
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {
        apps.length === 0 && (
          <div className="text-center py-12">
            <Server className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No applications</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new application.</p>
            <div className="mt-6">
              <Link
                to="/apps/new"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Application
              </Link>
            </div>
          </div>
        )
      }
    </div >
  );
}

export default Apps;